import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Invoices from './Invoices';
import authService from './api-authorization/AuthorizeService';
import RunsheetInvoiceItemsPopup from './RunSheetInvoiceItemsPopup';
import ConfirmPopups from './ConfirmPopup';

interface JobInvoiceProps {
    jobId: number;
    jobStatusId: number;
    isEvent: boolean;
    isThreeDayHire: boolean;
}

interface JobInvoices {
    loading: boolean;
    hasPermission: boolean;
    items: ItemList[];
    itemTypes: ItemTypeList[];
    gst: number;
    invoiceDetails: InvoiceDetails[];
    invoiceTotalFormatted: string;
    canEditHirePeriod: boolean;
    canView: boolean;
    canSend: boolean;
    canUpdatePaid: boolean;
    canCreate: boolean;
    page: number;
    totalRecords: number;
    pageSize: number;
    extraInvoiceItem: ExtraInvoiceItem;
    showInvoiceItemModal: boolean;
    showConfirm: boolean;
    showConfirmProgress: boolean;
    isEpaJob: boolean;
    saveDisabled: boolean;
}

interface InvoiceDetails {
    id: number;
    companyId: number;          //job company id
    invoiceCompanyId: number;   //invoice company id => used to send to the correct Reckon
    customer: string;
    customerId: number;
    customerType: string;
    jobId: number;
    siteAddress: string;
    type: string;
    sendTo: string;
    batchNumber: string;
    reckonInvoiceId: string;
    canUpdateReckonInvoiceId: boolean;
    quoteNumber: string;
    hirePeriod: string;
    invoiceDate: string;
    dueDate: string;
    paidDate: string;
    paidDateActual: string;
    amount: string;
    errorMessage: string;
    status: string;
    statusId: number;
    canEdit: boolean;
    canDelete: boolean;
    invoiceStartDate: string;
    invoiceStartDateFormatted: string;
    isDirty: boolean;
    items: InvoiceItems[];
    showDetail: boolean;
    errors: any;
}

interface InvoiceItems {
    itemIndex: number;
    id: number;
    jobInvoiceId: number;
    branchIdent: string;
    item: string;
    itemId: number;
    itemTypeId: number;
    invItemType: string;
    invItemTypeId: number;
    quantityType: string;
    purchaseOrderNumber: string;
    additionalHireRate: string;
    rate: string;
    rateFormatted: string;
    gst: string;
    gstFormatted: string;
    chargeGst: boolean;
    amount: string;
    amountFormatted: string;
    description: string;
    status: number;
    invoiceStartDate: string;
    invoiceEndDate: string;
    invoiceStartDateFormatted: string;
    invoiceEndDateFormatted: string;
    hirePeriodDays: string;
    hirePeriod: string;
    dayRate: string;
    weekMonthRate: string;
    isDirty: boolean;
}

interface ExtraInvoiceItem {
    noCalloutInvoiceNotes: string;
    calloutNotes: string;
    invoiceItems: ExtraInvoiceItems[];
}

interface ExtraInvoiceItems {
    rowIndex: number;
    id: number;
    itemId: number;
    itemTypeId: number;
    scheduleId: number;
    jobQuoteItemId: number;
    rate: number;
    gst: number;
    amount: number;
    description: string;
    active: boolean;
}

interface ItemTypeList {
    id: number;
    name: string;
    chargeGst: boolean;
}

interface ItemList {
    id: number;
    jobQuoteItemId: number;
    name: string;
    chargeGst: boolean;
}

export default class JobInvoice extends React.Component<JobInvoiceProps, JobInvoices> {
    constructor(props: JobInvoiceProps) {
        super(props);
        this.state = {
            loading: true,
            items: [],
            itemTypes: [],
            gst: 0.1,
            invoiceDetails: [],
            invoiceTotalFormatted: "$0.00",
            canEditHirePeriod: false,
            canView: true,
            canSend: false,
            canUpdatePaid: false,
            canCreate: false,
            page: 1,
            totalRecords: 0,
            pageSize: 30,
            hasPermission: true,
            extraInvoiceItem: {
                invoiceItems: [],
                calloutNotes: "",
                noCalloutInvoiceNotes: ""
            },
            showInvoiceItemModal: false,
            showConfirm: false,
            showConfirmProgress: false,
            isEpaJob: false,
            saveDisabled: false
        }
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=ViewJobInvoices&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                this.getInvoices(1, true);
            } else {
                this.setState({ hasPermission: false, loading: false });
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    async getInvoices(page: number, getTotalRecords: boolean) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var totalRecords = getTotalRecords ? 0 : this.state.totalRecords;
        var jobId = this.props.jobId;

        axios.get('api/Job/GetInvoiceDetails?jobId=' + jobId + '&page=' + page + '&totalRecords=' + totalRecords + '&getTotalRecords=' + getTotalRecords + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var invoiceDetails = res.data.invoices;
            for (var i = 0; i < invoiceDetails.length; i++) {
                var invoice = invoiceDetails[i];
                invoice.errors = {};

                for (var y = 0; y < invoice.items.length; y++) {
                    invoice.items[y].itemIndex = y;
                    invoice.items[y].amount = invoice.items[y].amount.toFixed(2);
                }
            }

            this.setState({ itemTypes: res.data.itemTypes, isEpaJob: res.data.isEpaJob, items: res.data.jobItems, invoiceDetails: invoiceDetails, invoiceTotalFormatted: res.data.invoiceTotalFormatted, page: page, totalRecords: res.data.totalRecords, pageSize: res.data.pageSize, hasPermission: res.data.canView, canEditHirePeriod: res.data.canEditHirePeriod, canCreate: res.data.canCreate, loading: false, saveDisabled: false });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    save = (invoices: InvoiceDetails[]) => {
        if (invoices.length > 0) {
            this.saveInvoices(invoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    async saveInvoices(invoices: InvoiceDetails[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                customerId: invoices[inv].customerId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var saveInvoice = {
            invoiceDetails: invoiceitems,
            subId: user.sub
        };

        //save
        axios.post('api/Invoicing/SaveInvoiceItems', saveInvoice, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.getInvoices(this.state.page, false);
            toast.success("Invoices Saved");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        this.setState({ invoiceDetails: updatedInvoices });
    }

    pageChanged = (page: number) => {
        this.setState({ page: page });
        this.getInvoices(page, false);
    }

    saveHirePeriod = (invoices: InvoiceDetails[]) => {
        this.saveInvoiceHirePeriod(invoices);
    }

    async saveInvoiceHirePeriod(invoices: InvoiceDetails[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        if (invoices.length > 0) {
            var invoiceitems = [];
            for (var inv = 0; inv < invoices.length; inv++) {
                var item = {
                    jobId: invoices[inv].jobId,
                    invoiceItems: invoices[inv].items.filter(i => i.isDirty)
                }
                invoiceitems.push(item);
            }

            var saveInvoice = {
                invoiceDetails: invoiceitems,
                subId: user.sub
            };

            //save
            axios.post('api/Invoicing/SaveInvoiceItemHirePeriod', saveInvoice, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                this.getInvoices(this.state.page, false);
                toast.success("Invoices Saved");
            })
            .catch(error => {
                toast.error(error.message);
            });
        } else {
            toast.info("No invoices have been changed");
        }
    }

    //ADD INVOICE ITEM
    addInvoiceItems = (e: any) => {
        e.preventDefault();

        //add blank invoice item
        var invItems = this.state.extraInvoiceItem;
        var invItem = this.addBlankInvoiceItem();
        invItems.invoiceItems.push(invItem);

        this.setState({ showInvoiceItemModal: true, extraInvoiceItem: invItems });
    }

    addBlankInvoiceItem = () => {
        var invItem: ExtraInvoiceItems = {
            id: 0,
            rowIndex: 0,
            active: true,
            itemId: -1,
            itemTypeId: -1,
            scheduleId: 0,
            jobQuoteItemId: 0,
            description: "",
            rate: 0,
            gst: 0,
            amount: 0
        };
        return invItem;
    }

    hideInvoiceModal = () => {
        let invItem = this.resetExtraInvoiceItem();
        this.setState({ extraInvoiceItem: invItem, showInvoiceItemModal: false });
    }

    updateInvoiceItems = (invItem: ExtraInvoiceItem) => {
        this.setState({ extraInvoiceItem: invItem });
    }

    saveInvoiceItems = () => {
        this.saveExtraInvoiceItems();
    }

    saveExtraInvoiceItems = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var extraInvItems = this.state.extraInvoiceItem.invoiceItems;
        extraInvItems.pop();

        var invItems = {
            jobId: this.props.jobId,
            items: extraInvItems,
            subId: user.sub
        };

        //save
        axios.post('api/RunSheet/SaveExtraInvoiceItems', invItems, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Extra Item Invoice Created");

            //reset
            let invItem = this.resetExtraInvoiceItem();
            this.setState({ extraInvoiceItem: invItem, showInvoiceItemModal: false });
            this.getInvoices(1, true);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    resetExtraInvoiceItem = () => {
        var invoiceItem: ExtraInvoiceItem = {
            invoiceItems: [],
            calloutNotes: "",
            noCalloutInvoiceNotes: ""
        };

        return invoiceItem;
    }
    //END ADD INVOICE ITEM

    //Re-create Initial Invoice
    recreateInvoice = (e: any) => {
        e.preventDefault();

        this.setState({ showConfirm: true });
    }

    hideConfirm = () => {
        this.setState({ showConfirm: false, showConfirmProgress: false, saveDisabled: false });
    }

    saveRecreateInitialInvoice = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var recreate = {
            jobId: this.props.jobId,
            subId: user.sub
        };

        axios.post('api/Invoicing/RecreateInitialInvoice', recreate, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                this.setState({ showConfirm: false });
                    
                toast.success("Initial Invoice recreated successfully");
                this.getInvoices(1, true);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //End Re-create Initial Invoice

    //Create a $5K progress invoice for EPA
    createProgressInvoice = (e: any) => {
        e.preventDefault();

        if (this.state.saveDisabled) {
            return;
        } else {
            this.setState({ saveDisabled: true });
        }

        this.setState({ showConfirmProgress: true });
    }

    saveProgressInvoice = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var progress = {
            jobId: this.props.jobId,
            subId: user.sub
        };

        axios.post('api/Invoicing/CreateProgressInvoice', progress, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                this.setState({ showConfirmProgress: false });

                toast.success("Progress Invoice created successfully");
                this.getInvoices(1, true);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //End Create a Progress Invoice

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return (<div>
            {contents}
        </div>);
    }

    renderDetails = () => {
        var contents = <Invoices invoiceDetails={this.state.invoiceDetails} invoiceTotalFormatted={this.state.invoiceTotalFormatted} showCustType={false} showJobInfo={false} showQuoteNo={false} showHirePeriod={true} showStatus={true} showStartDate={false} showPaidDate={true} showPaid={false} showSend={false} showReckonInvNo={true} showRemove={false} showSentTo={true} showError={true} showSent={false} canEdit={false} canEditHirePeriod={this.state.canEditHirePeriod} canSend={false} canUpdatePaid={false} page={this.state.page} pageSize={this.state.pageSize} totalRecords={this.state.totalRecords} pageChanged={this.pageChanged} beforeValid={this.updateInvoiceDetails} afterValid={this.save} saveHirePeriod={this.saveHirePeriod} />
        let addInvoice = <RunsheetInvoiceItemsPopup showItems={true} showModal={this.state.showInvoiceItemModal} items={this.state.items} itemTypes={this.state.itemTypes} gst={this.state.gst} invoiceItem={this.state.extraInvoiceItem} invoiceItemModalType={1} hideModal={this.hideInvoiceModal} updateData={this.updateInvoiceItems} saveModal={this.saveInvoiceItems} />;
        let confirmRecreate = <ConfirmPopups heading="Cancel and Re-create Initial Invoice" text="This will cancel all existing initial invoices and re-create them based on what items have been installed. Continue?" showConfirmModal={this.state.showConfirm} hideConfirmModal={this.hideConfirm} saveConfirmModal={this.saveRecreateInitialInvoice} />
        let confirmProgress = <ConfirmPopups heading="Create a $5K Progress Invoice" text="This will create a $5,000 progress invoice for the customer. Continue?" showConfirmModal={this.state.showConfirmProgress} hideConfirmModal={this.hideConfirm} saveConfirmModal={this.saveProgressInvoice} />

        return (
            <div>
                <div className="static-modal">
                    {addInvoice}
                    {confirmRecreate}
                    {confirmProgress}
                </div>
                <div className={this.state.hasPermission ? "hidden" : ""}>
                    <h2 className="error">You do not have permission to view this page!</h2>
                </div>
                <div className={this.state.canCreate && this.props.jobStatusId != 7 ? "colAlignRight marginBottom10" : "hidden"}>
                    <button className={this.props.isEvent || this.props.isThreeDayHire ? "defaultbutton defaultbutton marginRight10" : "hidden"} type='button' title="Cancel exisiting initial invoice and create a new initial invoice based on what has been installed" onClick={this.recreateInvoice}>Re-create Initial Invoice</button>
                    <button className={this.state.isEpaJob ? "defaultbutton defaultbutton marginRight10" : "hidden"} type='button' title="Create a $5K progress invoice for the customer" onClick={this.createProgressInvoice} disabled={this.state.saveDisabled}>Create $5K Progress Invoice</button>
                    <button className="defaultbutton defaultbutton__small" type='button' title="Create an invoice for extra charges for the job/item" onClick={this.addInvoiceItems}>Add New</button>
                </div>
                <div className={this.state.hasPermission ? "" : "hidden"}>
                    {contents}
                </div>
            </div>
        )
    }

}